import React, { useState, useEffect, useCallback } from "react";
import { ethers } from "ethers";
import axios from "axios";
import BaitLogo from "../BaitLogo.png";
import Logo from "../mwallet.png";
import { ExternalLink } from "lucide-react";

import {
  Select,
} from "antd";

import {
  LogoutOutlined,
  ArrowLeftOutlined,
  SwapOutlined,
  CopyOutlined,
  ReloadOutlined,
  LineChartOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

// ------------------------------ CHAIN CONFIGURATION ---------------------------------
const CHAINS_CONFIG = {
  polygon: {
    name: "Polygon",
    chainKey: "polygon",
    chainIds: ["0x89", "137", "polygon", "matic"],
    rpc: "https://polygon-bor.publicnode.com",
    chainId: 137,
    router: "0xedf6066a2b290C185783862C7F4776A2C8077AD1",
    nativeToken: { 
      symbol: "POL", 
      displaySymbol: "POL",
      name: "Polygon",
      isNative: true, 
      address: null 
    },
    wrappedNative: "0x0d500B1d8E8eF31E21C99d1Db9A6444d3ADf1270",
    usdt: { 
      symbol: "USDT0", 
      displaySymbol: "USDT0",
      name: "Tether USD",
      address: "0xc2132D05D31c914a87C6611C10748AEb04B58e8F", 
      decimals: 6 
    },
    logo: "https://cryptologos.cc/logos/polygon-matic-logo.png",
    nativeLogo: "https://cryptologos.cc/logos/polygon-matic-logo.png",
    usdtLogo: "https://cryptologos.cc/logos/tether-usdt-logo.png",
  },
  goldchain: {
    name: "GoldChain",
    chainKey: "goldchain",
    chainIds: ["0x777771", "777771", "goldchain", "gold"],
    rpc: "https://rpc.goldchain.tech",
    chainId: 777771,
    router: "0x2e91eda8603554b301d37aa25dee3BC2CD6280d8",
    nativeToken: { 
      symbol: "BAIT", 
      displaySymbol: "BAIT",
      name: "Gold Backed Token",
      isNative: true, 
      address: null 
    },
    wrappedNative: "0x6e7d2f4fCa2ac4196072672a70105198036a170f",
    usdt: { 
      symbol: "WUSDT", 
      displaySymbol: "WUSDT",
      name: "Wrapped USDT",
      address: "0x4791394e566020D7a69A6029D4FD1324B9706107", 
      decimals: 6 
    },
    logo: BaitLogo,
    nativeLogo: BaitLogo,
    usdtLogo: "https://cryptologos.cc/logos/tether-usdt-logo.png",
  },
};

const ERC20_ABI = [
  "function balanceOf(address) view returns (uint256)",
  "function decimals() view returns (uint8)",
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
];

const ROUTER_ABI = [
  "function getAmountsOut(uint256 amountIn, address[] calldata path) view returns (uint256[] memory)",
  "function swapExactETHForTokens(uint256 amountOutMin, address[] calldata path, address to, uint256 deadline) payable returns (uint256[] memory)",
  "function swapExactTokensForETH(uint256 amountIn, uint256 amountOutMin, address[] calldata path, address to, uint256 deadline) returns (uint256[] memory)",
];

// =======================================================
// SWAP ACTIVITY STORAGE
// =======================================================

const SWAP_ACTIVITY_KEY = "baitpay:swapActivity";
const SWAP_ACTIVITY_LIMIT = 50;

const readSwapActivity = () => {
  try {
    const raw = localStorage.getItem(SWAP_ACTIVITY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeSwapActivity = (list) => {
  try {
    const trimmed = list.slice(0, SWAP_ACTIVITY_LIMIT);
    localStorage.setItem(
      SWAP_ACTIVITY_KEY,
      JSON.stringify(trimmed)
    );
  } catch (err) {
    console.error("Failed to persist swap activity:", err);
  }
};

const addSwapActivityEntry = (entry) => {
  const list = readSwapActivity();
  const next = [entry, ...list].slice(0, SWAP_ACTIVITY_LIMIT);
  writeSwapActivity(next);
  return next;
};

const getSwapExplorerUrl = (chainKey, txHash) => {
  if (!txHash) return "#";
  if (chainKey === "polygon") {
    return `https://polygonscan.com/tx/${txHash}`;
  }
  return `https://baitcoinexplorer.bait-coin.com/#/transaction/${txHash}`;
};

const formatSwapDate = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;

  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const getGoldPeg = async () => {
  try {
    const { data } = await axios.get("https://goldapi.bait-coin.com/api/gold-reference");
    return data?.bait?.price_usd || 614.69;
  } catch {
    return 614.69;
  }
};

// Helper function to determine chain from any identifier
const detectChainFromProp = (prop) => {
  if (!prop) return "goldchain";
  
  const propStr = prop.toString().toLowerCase();
  
  if (propStr === "0x89" || propStr === "137" || propStr === "polygon" || propStr === "matic") {
    return "polygon";
  }
  
  if (propStr === "0x777771" || propStr === "777771" || propStr === "goldchain" || propStr === "gold") {
    return "goldchain";
  }
  
  return "goldchain";
};

// -----------------------------------------------------------------------------
function SwapInWallet({

    wallet: propWallet,
    balance: propBalance,
    seedPhrase,

    selectedChain: propSelectedChain,

    setSelectedChain,

    chainOptions,

    logout,

    onBack

}) {
  // State
  const [chainKey, setChainKey] = useState(() => detectChainFromProp(propSelectedChain));
  const [isFlipped, setIsFlipped] = useState(false); // false = native->token, true = token->native
  const [amount, setAmount] = useState("");
  const [estimated, setEstimated] = useState("");
  const [pegRate, setPegRate] = useState(null);
  const [status, setStatus] = useState("Ready");
  const [tokenBalance, setTokenBalance] = useState("0");
  const [nativeBalance, setNativeBalance] = useState(propBalance || "0");
  const [isLoadingTokenBalance, setIsLoadingTokenBalance] = useState(false);
  const [wallet, setWallet] = useState(propWallet || null);
  const [privateKey, setPrivateKey] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);

  // Recent swap activity (filtered by current wallet)
  const [swapActivity, setSwapActivity] = useState([]);

  // Update wallet when prop changes
  useEffect(() => {
    if (propWallet) setWallet(propWallet);
  }, [propWallet]);

  // Update native balance when prop changes
  useEffect(() => {
    if (propBalance !== undefined) {
      setNativeBalance(propBalance);
    }
  }, [propBalance]);

  // Load swap activity when wallet changes
  useEffect(() => {
    if (!wallet) {
      setSwapActivity([]);
      return;
    }
    const all = readSwapActivity();
    const mine = all.filter(
      (e) =>
        String(e.wallet || "").toLowerCase() ===
        wallet.toLowerCase()
    );
    setSwapActivity(mine);
  }, [wallet]);

  // Update chain when selectedChain prop changes
  useEffect(() => {
    if (propSelectedChain !== undefined) {
      const newChainKey = detectChainFromProp(propSelectedChain);
      if (newChainKey !== chainKey) {
        console.log(`🔄 Chain changed: ${chainKey} -> ${newChainKey}`);
        setChainKey(newChainKey);
        setIsFlipped(false); // Reset flip when chain changes
        setAmount("");
        setEstimated("");
        setTokenBalance("0");
      }
    }
  }, [propSelectedChain]);

  // Load private key
  useEffect(() => {
    const loadPrivateKey = async () => {
      if (seedPhrase && !privateKey) {
        try {
          const walletFromSeed = ethers.Wallet.fromPhrase(seedPhrase);
          setPrivateKey(walletFromSeed.privateKey);
        } catch (err) {
          console.error("Error deriving private key:", err);
        }
      } else if (!privateKey) {
        const stored = localStorage.getItem("privateKey");
        if (stored) setPrivateKey(stored);
      }
    };
    loadPrivateKey();
  }, [seedPhrase, privateKey]);

  const currentChain = CHAINS_CONFIG[chainKey];
  
  // Initialize provider and signer
  useEffect(() => {
    if (!currentChain) return;
    
    try {
      const newProvider = new ethers.JsonRpcProvider(currentChain.rpc);
      setProvider(newProvider);
      
      if (privateKey) {
        const newSigner = new ethers.Wallet(privateKey, newProvider);
        setSigner(newSigner);
      }
    } catch (err) {
      console.error("Provider init error:", err);
      setStatus("❌ Failed to connect to network");
    }
  }, [chainKey, privateKey, currentChain]);

  // Fetch token balance
  const fetchTokenBalance = useCallback(async () => {
    if (!provider || !wallet || !currentChain) {
      setTokenBalance("0");
      return;
    }
    
    setIsLoadingTokenBalance(true);
    try {
      const tokenContract = new ethers.Contract(currentChain.usdt.address, ERC20_ABI, provider);
      const tokenBalanceWei = await tokenContract.balanceOf(wallet);
      const tokenBalanceFormatted = ethers.formatUnits(tokenBalanceWei, currentChain.usdt.decimals);
      setTokenBalance(tokenBalanceFormatted);
    } catch (err) {
      console.error("Token balance error:", err);
      setTokenBalance("0");
    } finally {
      setIsLoadingTokenBalance(false);
    }
  }, [provider, wallet, currentChain]);

  useEffect(() => {
    if (provider && wallet && currentChain) {
      fetchTokenBalance();
    }
  }, [provider, wallet, currentChain, fetchTokenBalance]);

  // Refresh peg (GoldChain only)
  const refreshPeg = async () => {
    if (chainKey !== "goldchain") {
      setStatus("Peg only available on GoldChain");
      return;
    }
    setStatus("Fetching BAIT price...");
    try {
      const peg = await getGoldPeg();
      setPegRate(peg.toFixed(2));
      setStatus(`✅ 1 BAIT ≈ ${peg.toFixed(2)} WUSDT`);
    } catch {
      setStatus("❌ Failed to fetch peg");
    }
  };

  useEffect(() => {
    if (chainKey === "goldchain") {
      refreshPeg();
    } else {
      setPegRate(null);
    }
  }, [chainKey]);

  // Flip tokens (swap direction) - called when clicking the arrow
  const flipTokens = () => {
    setIsFlipped(!isFlipped);
    setAmount("");
    setEstimated("");
    setStatus("Ready");
  };

  // Get current from/to symbols based on flip state
  const getCurrentSymbols = () => {
    if (isFlipped) {
      // Selling token (USDT0/WUSDT) to buy native (POL/BAIT)
      return {
        fromSymbol: currentChain?.usdt?.displaySymbol || currentChain?.usdt?.symbol,
        toSymbol: currentChain?.nativeToken?.displaySymbol || currentChain?.nativeToken?.symbol,
        fromName: currentChain?.usdt?.name || currentChain?.usdt?.symbol,
        toName: currentChain?.nativeToken?.name || currentChain?.nativeToken?.symbol,
        fromBalance: tokenBalance,
        toBalance: nativeBalance,
        isSellingNative: false,
        fromLogo: currentChain?.usdtLogo,
        toLogo: currentChain?.nativeLogo,
      };
    } else {
      // Selling native (POL/BAIT) to buy token (USDT0/WUSDT)
      return {
        fromSymbol: currentChain?.nativeToken?.displaySymbol || currentChain?.nativeToken?.symbol,
        toSymbol: currentChain?.usdt?.displaySymbol || currentChain?.usdt?.symbol,
        fromName: currentChain?.nativeToken?.name || currentChain?.nativeToken?.symbol,
        toName: currentChain?.usdt?.name || currentChain?.usdt?.symbol,
        fromBalance: nativeBalance,
        toBalance: tokenBalance,
        isSellingNative: true,
        fromLogo: currentChain?.nativeLogo,
        toLogo: currentChain?.usdtLogo,
      };
    }
  };

  // Estimate swap output
  const estimateSwap = async () => {
    if (!provider || !amount || parseFloat(amount) <= 0) {
      alert("Enter a valid amount");
      return;
    }
    
    const { isSellingNative, fromSymbol } = getCurrentSymbols();
    const fromBalanceCheck = isSellingNative ? nativeBalance : tokenBalance;
    
    if (parseFloat(fromBalanceCheck) < parseFloat(amount)) {
      alert(`Insufficient ${fromSymbol} balance`);
      return;
    }
    
    setStatus("Estimating...");
    try {
      let outAmount;
      
      if (isSellingNative) {
        // Selling native (POL/BAIT) for token (USDT0/WUSDT)
        const router = new ethers.Contract(currentChain.router, ROUTER_ABI, provider);
        const path = [currentChain.wrappedNative, currentChain.usdt.address];
        const amountIn = ethers.parseEther(amount);
        const amountsOut = await router.getAmountsOut(amountIn, path);
        outAmount = ethers.formatUnits(amountsOut[1], currentChain.usdt.decimals);
      } else {
        // Selling token (USDT0/WUSDT) for native (POL/BAIT)
        const router = new ethers.Contract(currentChain.router, ROUTER_ABI, provider);
        const path = [currentChain.usdt.address, currentChain.wrappedNative];
        const amountIn = ethers.parseUnits(amount, currentChain.usdt.decimals);
        const amountsOut = await router.getAmountsOut(amountIn, path);
        outAmount = ethers.formatEther(amountsOut[1]);
      }
      
      setEstimated(outAmount);
      setStatus("Estimate ready");
    } catch (err) {
      console.error(err);
      setStatus("Estimate failed – check liquidity");
    }
  };

  // Execute swap
  const handleSwap = async () => {
    if (!signer) {
      alert("No private key found. Please create or import a wallet in Settings.");
      setStatus("❌ Swap requires a valid private key");
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      alert("Enter a valid amount");
      return;
    }
    
    const { isSellingNative, fromSymbol, toSymbol } = getCurrentSymbols();
    const fromBalanceCheck = isSellingNative ? nativeBalance : tokenBalance;
    
    if (parseFloat(fromBalanceCheck) < parseFloat(amount)) {
      alert(`Insufficient ${fromSymbol} balance`);
      return;
    }

    // Snapshot for the activity log
    const activityChainKey = chainKey;
    const activityFromSymbol = fromSymbol;
    const activityToSymbol = toSymbol;
    const activityAmount = amount;
    const activityIsSellingNative = isSellingNative;
    
    setStatus("Processing swap...");
    try {
      const router = new ethers.Contract(currentChain.router, ROUTER_ABI, signer);
      const userAddress = await signer.getAddress();
      const deadline = Math.floor(Date.now() / 1000) + 60 * 10;

      let tx;
      let expectedOut = "";

      if (isSellingNative) {
        // Swap native (POL/BAIT) for token (USDT0/WUSDT)
        const path = [currentChain.wrappedNative, currentChain.usdt.address];
        const amountIn = ethers.parseEther(amount);
        const amountsOut = await router.getAmountsOut(amountIn, path);
        const minOut = (amountsOut[1] * 995n) / 1000n;

        expectedOut = ethers.formatUnits(
          amountsOut[1],
          currentChain.usdt.decimals
        );
        
        tx = await router.swapExactETHForTokens(
          minOut,
          path,
          userAddress,
          deadline,
          { value: amountIn, gasLimit: 300000 }
        );
        await tx.wait();
      } else {
        // Swap token (USDT0/WUSDT) for native (POL/BAIT)
        const tokenAddress = currentChain.usdt.address;
        const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
        const amountIn = ethers.parseUnits(amount, currentChain.usdt.decimals);
        
        console.log(`Swapping ${amount} ${currentChain.usdt.symbol} for ${currentChain.nativeToken.symbol}`);
        console.log(`Token address: ${tokenAddress}`);
        console.log(`Router address: ${currentChain.router}`);
        
        let allowance;
        try {
          allowance = await tokenContract.allowance(userAddress, currentChain.router);
          console.log(`Allowance: ${allowance.toString()}`);
        } catch (allowanceErr) {
          console.error("Allowance check error:", allowanceErr);
          allowance = 0n;
        }
        
        if (allowance < amountIn) {
          setStatus(`Approving ${currentChain.usdt.displaySymbol}...`);
          try {
            const approveTx = await tokenContract.approve(currentChain.router, amountIn);
            await approveTx.wait();
            console.log("Approval successful");
          } catch (approveErr) {
            console.error("Approval error:", approveErr);
            throw new Error(`Failed to approve ${currentChain.usdt.displaySymbol}. Please try again.`);
          }
        }
        
        const path = [currentChain.usdt.address, currentChain.wrappedNative];
        const amountsOut = await router.getAmountsOut(amountIn, path);
        const minOut = (amountsOut[1] * 995n) / 1000n;

        expectedOut = ethers.formatEther(amountsOut[1]);
        
        console.log(`Expected output: ${ethers.formatEther(amountsOut[1])} ${currentChain.nativeToken.symbol}`);
        
        tx = await router.swapExactTokensForETH(
          amountIn,
          minOut,
          path,
          userAddress,
          deadline,
          { gasLimit: 500000 }
        );
        await tx.wait();
      }

      // ── Record this swap in the activity log
      const entry = {
        id: tx.hash,
        wallet: wallet.toLowerCase(),
        chain: activityChainKey,
        chainName: currentChain.name,
        fromSymbol: activityFromSymbol,
        toSymbol: activityToSymbol,
        amount: activityAmount,
        expectedOut: expectedOut,
        isSellingNative: activityIsSellingNative,
        txHash: tx.hash,
        status: "completed",
        createdAt: Date.now(),
      };

      const updatedList = addSwapActivityEntry(entry);
      setSwapActivity(
        updatedList.filter(
          (e) =>
            String(e.wallet).toLowerCase() ===
            wallet.toLowerCase()
        )
      );
      
      setStatus("✅ Swap successful!");
      setAmount("");
      setEstimated("");
      await fetchTokenBalance();
      window.dispatchEvent(new CustomEvent('refreshBalances'));
    } catch (err) {
      console.error("Swap error:", err);
      let reason = err.reason || err.message || err.toString();
      if (reason.includes("INSUFFICIENT_OUTPUT_AMOUNT")) {
        reason = "Slippage too high. Try again with a smaller amount or increase slippage tolerance.";
      } else if (reason.includes("TRANSFER_FROM_FAILED")) {
        reason = "Transaction failed. Check your balance and try again.";
      } else if (reason.includes("execution reverted")) {
        reason = "The swap failed. This could be due to insufficient liquidity or a temporary issue.";
      } else if (reason.includes("missing revert data")) {
        reason = "Network error. Please check your connection and try again.";
      }
      setStatus(`❌ Swap failed: ${reason.slice(0, 150)}`);
    }
  };

  // Clear swap activity for this wallet
  const clearSwapActivity = () => {
    if (!wallet) return;
    const all = readSwapActivity();
    const others = all.filter(
      (e) =>
        String(e.wallet || "").toLowerCase() !==
        wallet.toLowerCase()
    );
    writeSwapActivity(others);
    setSwapActivity([]);
  };

  const { 
    fromSymbol, 
    toSymbol, 
    fromName, 
    toName,
    fromBalance: currentFromBalance, 
    toBalance: currentToBalance,
    fromLogo,
    toLogo,
    isSellingNative
  } = getCurrentSymbols();
  
  const fromBalanceDisplay = isLoadingTokenBalance && !isSellingNative ? "..." : parseFloat(currentFromBalance || "0").toFixed(isFlipped ? 6 : 4);
  const toBalanceDisplay = isLoadingTokenBalance && isSellingNative ? "..." : parseFloat(currentToBalance || "0").toFixed(isFlipped ? 4 : 6);

  if (!wallet) {
    return (
      <div style={styles.container}>
        <p style={{ textAlign: "center", color: "#ef4444" }}>
          ⚠️ No wallet detected. Please go to Settings and create or import a wallet.
        </p>
      </div>
    );
  }

  if (!currentChain) {
    return (
      <div style={styles.container}>
        <p style={{ textAlign: "center", color: "#ef4444" }}>
          ⚠️ Invalid chain configuration for: {chainKey}
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Chain Indicator */}
      <div style={styles.chainIndicator}>
        <img src={currentChain.logo} alt={currentChain.name} style={styles.chainIconSmall} />
        <span style={styles.chainName}>{currentChain.name}</span>
      </div>

      {/* Token Pair Header */}
      <div style={styles.tokenPairContainer}>
        <div style={styles.tokenPair}>
          <div style={styles.tokenPairItem}>
            <img src={fromLogo} alt={fromSymbol} style={styles.tokenIcon} />
            <div>
              <div style={styles.tokenPairSymbol}>{fromSymbol}</div>
              <div style={styles.tokenPairName}>{fromName}</div>
            </div>
          </div>
          <span 
            onClick={flipTokens} 
            style={styles.arrowIcon}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            ↔
          </span>
          <div style={styles.tokenPairItem}>
            <img src={toLogo} alt={toSymbol} style={styles.tokenIcon} />
            <div>
              <div style={styles.tokenPairSymbol}>{toSymbol}</div>
              <div style={styles.tokenPairName}>{toName}</div>
            </div>
          </div>
        </div>
        
        <p style={styles.walletAddress}>
          Wallet: {wallet.slice(0, 6)}...{wallet.slice(-4)}
        </p>
      </div>

      {/* Balances */}
      <div style={styles.balancesContainer}>
        <div style={styles.balanceItem}>
          <span style={styles.balanceLabel}>{fromSymbol} balance:</span>
          <span style={styles.balanceValue}>{fromBalanceDisplay}</span>
        </div>
        <div style={styles.balanceItem}>
          <span style={styles.balanceLabel}>{toSymbol} balance:</span>
          <span style={styles.balanceValue}>{toBalanceDisplay}</span>
        </div>
      </div>

      {/* Contract Address */}
      {!isFlipped && (
        <div style={styles.contractAddress}>
          <span style={styles.contractLabel}>Contract:</span>
          <span 
            style={styles.contractValue} 
            onClick={() => navigator.clipboard.writeText(currentChain.usdt.address)}
            title="Click to copy"
          >
            {currentChain.usdt.address.slice(0, 6)}...{currentChain.usdt.address.slice(-4)} 📋
          </span>
        </div>
      )}

      {/* Peg Info */}
      {pegRate && chainKey === "goldchain" && !isFlipped && (
        <div style={styles.pegInfo}>
          <span style={styles.pegIcon}>$</span> 1 BAIT ≈ {pegRate} WUSDT
        </div>
      )}

      {/* Amount Input */}
      <div style={styles.inputGroup}>
        <div style={styles.inputHeader}>
          <span style={styles.inputLabel}>Amount ({fromSymbol})</span>
        </div>
        <div style={styles.inputWrapper}>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.0"
            style={styles.input}
          />
          <button 
            onClick={() => {
              const maxAmount = isFlipped ? tokenBalance : nativeBalance;
              setAmount(maxAmount.toString());
            }} 
            style={styles.maxButton}
          >
            MAX
          </button>
        </div>
      </div>

      {/* Estimated Output */}
      {estimated && (
        <div style={styles.estimatedOutput}>
          ≈ {parseFloat(estimated).toFixed(6)} {toSymbol}
        </div>
      )}

      {/* Buttons */}
      <div style={styles.buttonGroup}>
        {chainKey === "goldchain" && !isFlipped && (
          <button onClick={refreshPeg} style={styles.buttonSecondary}>
            <ReloadOutlined style={{ marginRight: 6 }} />
            Refresh Peg
          </button>
        )}
        <button onClick={estimateSwap} style={styles.buttonSecondary}>
          <LineChartOutlined style={{ marginRight: 6 }} />
          Estimate
        </button>
        <button onClick={handleSwap} style={styles.buttonPrimary}>
          Swap Now
        </button>
      </div>

      {/* Footer Section - Exact match from reference */}
      <div style={styles.footerSection}>
        <div style={styles.statusContainer}>
          <span style={styles.statusText}>{status}</span>
        </div>
        
        <div style={styles.swapInfoContainer}>
          <div style={styles.swapInfoRow}>
            <span style={styles.swapInfoText}>
              You are swapping on <strong style={styles.swapInfoHighlight}>{currentChain.name}</strong> DEX
            </span>
          </div>
          <div style={styles.swapInfoRow}>
            <CheckCircleOutlined style={styles.verifyIcon} />
            <span style={styles.swapInfoSubtext}>
              Always verify token addresses before swapping.
            </span>
          </div>
        </div>
      </div>

      {/* SWAP ACTIVITY */}
      <div style={styles.activityCard}>
        <div style={styles.activityHeader}>
          <span style={styles.activityTitle}>
            Swap Activity
          </span>
          {swapActivity.length > 0 && (
            <button
              type="button"
              onClick={clearSwapActivity}
              style={styles.activityClear}
              title="Clear activity"
            >
              Clear
            </button>
          )}
        </div>

        {swapActivity.length === 0 ? (
          <div style={styles.activityEmpty}>
            <span style={styles.activityEmptyTitle}>
              No swap activity yet
            </span>
            <span style={styles.activityEmptyHint}>
              Your completed swaps will appear here
            </span>
          </div>
        ) : (
          <div style={styles.activityList}>
            {swapActivity.slice(0, 5).map((entry) => {
              const chainConf =
                CHAINS_CONFIG[entry.chain] || currentChain;
              const fromLogo =
                entry.fromSymbol === chainConf?.nativeToken?.displaySymbol
                  ? chainConf?.nativeLogo
                  : chainConf?.usdtLogo;
              const toLogo =
                entry.toSymbol === chainConf?.nativeToken?.displaySymbol
                  ? chainConf?.nativeLogo
                  : chainConf?.usdtLogo;

              return (
                <div key={entry.id} style={styles.activityItem}>
                  <div style={styles.activityLeft}>
                    <div style={styles.activityDirection}>
                      <img
                        src={fromLogo || BaitLogo}
                        alt=""
                        style={styles.activityTokenIcon}
                      />
                      <span style={styles.activityArrow}>
                        →
                      </span>
                      <img
                        src={toLogo || BaitLogo}
                        alt=""
                        style={styles.activityTokenIcon}
                      />
                    </div>

                    <div style={styles.activityDetails}>
                      <span style={styles.activityAmount}>
                        {entry.amount} {entry.fromSymbol} → {entry.toSymbol}
                      </span>
                      <span style={styles.activityMeta}>
                        {formatSwapDate(entry.createdAt)}
                        {entry.expectedOut &&
                          ` · ≈${parseFloat(entry.expectedOut).toFixed(6)} ${entry.toSymbol}`}
                      </span>
                    </div>
                  </div>

                  <div style={styles.activityRight}>
                    <span
                      style={{
                        ...styles.activityStatus,
                        ...(entry.status === "completed"
                          ? styles.activityStatusCompleted
                          : styles.activityStatusFailed),
                      }}
                    >
                      {entry.status === "completed"
                        ? "Completed"
                        : "Failed"}
                    </span>

                    <a
                      href={getSwapExplorerUrl(
                        entry.chain,
                        entry.txHash
                      )}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.activityLink}
                      title="View on explorer"
                    >
                      <ExternalLink size={12} />
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// =========================================================
// SWAP PAGE STYLES
// BAIT WALLET LIGHT / BRONZE THEME
// =========================================================

const styles = {

  /* =======================================================
     CHAIN INDICATOR
  ======================================================= */

  chainIndicator: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,

    margin: "0 auto 16px",

    padding: "3px 7px",

    background: "#fbf7ef",

    border: "1px solid #d9b77a",

    borderRadius: 30,

    width: "fit-content",

    boxSizing: "border-box",

    boxShadow: "0 3px 10px rgba(154, 106, 24, 0.08)",
  },


  chainIconSmall: {
    width: 18,
    height: 18,

    borderRadius: "50%",

    objectFit: "cover",

    border: "1px solid #d9b77a",

    flexShrink: 0,
  },


  chainName: {
    fontSize: 12,
    fontWeight: 700,

    color: "#9a6a18",

    letterSpacing: ".2px",
  },


  /* =======================================================
     TOKEN PAIR CARD
  ======================================================= */

  tokenPairContainer: {
  width: "calc(100% + 16px)",
  boxSizing: "border-box",

  background: "#ffffff",

  border: "1px solid #9a6a18",

  borderRadius: 13,

  padding: "10px 12px",

  marginLeft: -8,
  marginRight: -8,

  marginBottom: 12,
  },


  tokenPair: {
    display: "flex",

    alignItems: "center",

    justifyContent: "space-between",

    gap: 10,

    marginBottom: 12,

    width: "100%",
  },


  tokenPairItem: {
    display: "flex",

    alignItems: "center",

    gap: 9,

    minWidth: 0,

    flex: 1,
  },


  tokenIcon: {
    width: 38,
    height: 38,

    minWidth: 38,

    borderRadius: "50%",

    objectFit: "cover",

   

    padding: 2,

    boxSizing: "border-box",
  },


  tokenPairSymbol: {
    fontSize: 17,

    fontWeight: 800,

    color: "#172033",

    lineHeight: 1.2,
  },


  tokenPairName: {
    fontSize: 11,

    color: "#687386",

    fontWeight: 500,

    marginTop: 2,

    whiteSpace: "nowrap",

    overflow: "hidden",

    textOverflow: "ellipsis",

    maxWidth: 120,
  },


  /* =======================================================
     SWAP ARROW
  ======================================================= */

  arrowIcon: {
    width: 34,
    height: 34,

    minWidth: 34,

    display: "inline-flex",

    alignItems: "center",

    justifyContent: "center",

    fontSize: 19,

    fontWeight: 700,

    color: "#9a6a18",

    cursor: "pointer",

    

    borderRadius: "50%",

    transition: "all .25s ease",

    boxShadow: "0 2px 7px rgba(154, 106, 24, .10)",

    boxSizing: "border-box",
  },


  /* =======================================================
     WALLET ADDRESS
  ======================================================= */

  walletAddress: {
    display: "block",

    width: "fit-content",

    maxWidth: "100%",

    margin: 0,

    padding: "5px 10px",

    borderRadius: 8,

    background: "#fbf7ef",

    border: "1px solid #ccb42b",

    color: "#687386",

    fontSize: 11,

    fontFamily: "'SF Mono', 'Menlo', 'Monaco', monospace",

    letterSpacing: ".2px",

    boxSizing: "border-box",
  },


  /* =======================================================
     BALANCES CONTAINER
  ======================================================= */

  balancesContainer: {
     width: "calc(100% + 16px)",

    display: "flex",

    justifyContent: "space-between",

    gap: 10,

    background: "#ffffff",

     boxSizing: "border-box",

  background: "#ffffff",

  border: "1px solid #9a6a18",

  borderRadius: 13,

  padding: "10px 12px",

  marginLeft: -8,
  marginRight: -8,

  marginBottom: 12,
  },


  balanceItem: {
    display: "flex",

    flexDirection: "column",

    minWidth: 0,

    flex: 1,
  },


  balanceLabel: {
    fontSize: 10,

    color: "#a8b2c1",

    fontWeight: 700,

    textTransform: "uppercase",

    letterSpacing: ".6px",
  },


  balanceValue: {
    fontSize: 15,

    fontWeight: 800,

    color: "#172033",

    marginTop: 3,

    overflow: "hidden",

    textOverflow: "ellipsis",

    whiteSpace: "nowrap",
  },


  /* =======================================================
     CONTRACT ADDRESS
  ======================================================= */

  contractAddress: {
     width: "calc(100% + 16px)",

    display: "flex",

    justifyContent: "center",

    alignItems: "center",

    gap: 7,

     boxSizing: "border-box",

  background: "#fbf7ef",

  border: "1px solid #9a6a18",

  borderRadius: 13,

  padding: "10px 12px",

  marginLeft: -8,
  marginRight: -8,

  marginBottom: 12,

    overflow: "hidden",
  },


  contractLabel: {
    color: "#9a6a18",

    fontWeight: 700,

    flexShrink: 0,
  },


  contractValue: {
    color: "#9a6a18",

    cursor: "pointer",

    fontFamily: "'SF Mono', 'Menlo', 'Monaco', monospace",

    fontSize: 11,

    fontWeight: 600,

    overflow: "hidden",

    textOverflow: "ellipsis",

    whiteSpace: "nowrap",
  },


  /* =======================================================
     PEG INFORMATION
  ======================================================= */

  pegInfo: {
    width: "calc(100% + 16px)",

    background: "#fbf7ef",

    color: "#9a6a18",

   

    textAlign: "center",

    fontSize: 14,

    fontWeight: 800,

     boxSizing: "border-box",

  background: "#fbf7ef",

  border: "1px solid #9a6a18",

  borderRadius: 13,

  padding: "10px 12px",

  marginLeft: -8,
  marginRight: -8,

  marginBottom: 12,
  },


  pegIcon: {
    marginRight: 4,

    fontWeight: 800,

    color: "#9a6a18",
  },


  /* =======================================================
     AMOUNT INPUT GROUP
  ======================================================= */

  inputGroup: {
    width: "100%",

    marginBottom: 14,
  },


  inputHeader: {
    display: "flex",

    justifyContent: "space-between",

    alignItems: "center",

    marginBottom: 7,
  },


  inputLabel: {
    fontSize: 12,

    fontWeight: 700,

    color: "#687386",

    textTransform: "uppercase",

    letterSpacing: ".4px",
  },


  /* =======================================================
     INPUT WRAPPER
  ======================================================= */

  inputWrapper: {
    position: "relative",

    display: "flex",

    alignItems: "center",

    width: "calc(100% + 16px)",

    background: "#ffffff",

      boxSizing: "border-box",

  background: "#ffffff",

  border: "1px solid #9a6a18",

  borderRadius: 13,

  padding: "10px 12px",

  marginLeft: -8,
  marginRight: -8,

  marginBottom: 12,
  },


  /* =======================================================
     AMOUNT INPUT
  ======================================================= */

  input: {
    width: "100%",

    height: 48,

    padding: "10px 62px 10px 14px",

    fontSize: 19,

    fontWeight: 700,

    border: "none",

    borderRadius: 13,

    outline: "none",

    background: "transparent",

    color: "#172033",

    boxSizing: "border-box",

    fontFamily: "'Inter', sans-serif",
  },


/* =======================================================
   MAX BUTTON
======================================================= */

maxButton: {
  position: "absolute",

  right: 8,

  top: "50%",

  transform: "translateY(-50%)",

  background: "#fbf7ef",

  border: "1px solid #d9b77a",

  borderRadius: 6,

  color: "#9a6a18",

  fontSize: 11,

  fontWeight: 700,

  padding: "2px 10px",

  cursor: "pointer",

  transition: "all 0.2s ease",

  height: 22,

  width: 44,

  display: "flex",

  alignItems: "center",

  justifyContent: "center",

  letterSpacing: "0.3px",

  fontFamily: "'Inter', sans-serif",

  lineHeight: 1,

  boxSizing: "border-box",
},
  /* =======================================================
     ESTIMATED OUTPUT
  ======================================================= */

  estimatedOutput: {
    width: "100%",

    background: "#f7f9fc",

    color: "#9a6a18",

    padding: "10px 14px",

    borderRadius: 12,

    textAlign: "center",

    fontSize: 16,

    fontWeight: 800,

    marginBottom: 14,

    border: "1px solid #d9b77a",

    boxSizing: "border-box",
  },


  /* =======================================================
     BUTTON GROUP
  ======================================================= */

  buttonGroup: {
    display: "flex",

    gap: 8,

    width: "100%",

    marginBottom: 14,

    boxSizing: "border-box",
  },


  /* =======================================================
     SECONDARY BUTTON
  ======================================================= */

  buttonSecondary: {
    flex: 1,

    minWidth: 0,

    height: 42,

    padding: "8px 10px",

    background: "#ffffff",

    border: "1px solid #c9a15c",

    borderRadius: 11,

    fontWeight: 700,

    fontSize: 11,

    cursor: "pointer",

    color: "#9a6a18",

    transition: "all .25s ease",

    fontFamily: "'Inter', sans-serif",

    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    boxSizing: "border-box",

    boxShadow: "0 3px 8px rgba(23, 32, 51, .04)",
  },


  /* =======================================================
     PRIMARY SWAP BUTTON
  ======================================================= */

  buttonPrimary: {
    flex: 1.5,

    minWidth: 0,

    height: 42,

    padding: "8px 12px",

    background: "#9a6a18",

    border: "1px solid #9a6a18",

    borderRadius: 11,

    fontWeight: 800,

    fontSize: 12,

    cursor: "pointer",

    color: "#ffffff",

    transition: "all .25s ease",

    fontFamily: "'Inter', sans-serif",

    boxShadow: "0 4px 12px rgba(154, 106, 24, .20)",

    boxSizing: "border-box",
  },


  /* =======================================================
     FOOTER / STATUS SECTION
  ======================================================= */

  footerSection: {
     width: "calc(100% + 16px)",
    boxSizing: "border-box",

  background: "#ffffff",

  border: "1px solid #9a6a18",

  borderRadius: 13,

  padding: "10px 12px",

  marginLeft: -8,
  marginRight: -8,

  marginBottom: 12,
  },


  statusContainer: {
    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    paddingBottom: 8,

    borderBottom: "1px solid #e5e8ee",

    marginBottom: 8,
  },


  statusText: {
    fontSize: 11,

    color: "rgb(22, 22, 21)",

    fontFamily: "'Inter', sans-serif",

    textAlign: "center",
  },


  /* =======================================================
     SWAP INFORMATION
  ======================================================= */

  swapInfoContainer: {
    display: "flex",

    flexDirection: "column",

    gap: 4,
  },


  swapInfoRow: {
    display: "flex",

    alignItems: "center",

    justifyContent: "center",

    gap: 6,

    textAlign: "center",
  },


  swapInfoText: {
    fontSize: 12,

    color: "#171718",

    fontFamily: "'Inter', sans-serif",
  },


  swapInfoHighlight: {
    color: "#9a6a18",

    fontWeight: 800,
  },


  verifyIcon: {
    fontSize: 12,

    color: "#9a6a18",

    flexShrink: 0,
  },


  swapInfoSubtext: {
    fontSize: 11,

    color: "#0a0a0a",

    fontFamily: "'Inter', sans-serif",
  },

  /* =======================================================
     SWAP ACTIVITY
  ======================================================= */

  activityCard: {
    width: "calc(100% + 16px)",
    boxSizing: "border-box",

    background: "#ffffff",

    border: "1px solid #9a6a18",

    borderRadius: 13,

    padding: "10px 12px",

    marginLeft: -8,
    marginRight: -8,

    marginTop: 10,
    marginBottom: 12,
  },

  activityHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },

  activityTitle: {
    fontSize: 12,
    fontWeight: 800,
    color: "#9a6a18",
    letterSpacing: ".4px",
    textTransform: "uppercase",
  },

  activityClear: {
    background: "none",
    border: "none",
    color: "#a8b2c1",
    fontSize: 10,
    fontWeight: 700,
    cursor: "pointer",
    padding: 0,
    fontFamily: "'Inter', sans-serif",
    letterSpacing: ".3px",
    textTransform: "uppercase",
  },

  activityEmpty: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "14px 8px",
    borderRadius: 10,
    background: "#fbf7ef",
    border: "1px dashed #d9b77a",
    textAlign: "center",
    gap: 3,
  },

  activityEmptyTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#9a6a18",
    lineHeight: 1.2,
  },

  activityEmptyHint: {
    fontSize: 10,
    color: "#a8b2c1",
    lineHeight: 1.2,
  },

  activityList: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  activityItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "7px 9px",
    borderRadius: 10,
    background: "#fbf7ef",
    border: "1px solid #f0e2c4",
    gap: 8,
    minWidth: 0,
    boxSizing: "border-box",
  },

  activityLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    minWidth: 0,
    flex: 1,
  },

  activityDirection: {
    display: "flex",
    alignItems: "center",
    gap: 3,
    flexShrink: 0,
  },

  activityTokenIcon: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    objectFit: "cover",
    flexShrink: 0,
  },

  activityArrow: {
    color: "#9a6a18",
    fontSize: 10,
    fontWeight: 500,
    lineHeight: 1,
  },

  activityDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    minWidth: 0,
    flex: 1,
  },

  activityAmount: {
    fontSize: 11,
    fontWeight: 800,
    color: "#172033",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.15,
  },

  activityMeta: {
    fontSize: 9,
    color: "#a8b2c1",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: 1.15,
  },

  activityRight: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexShrink: 0,
  },

  activityStatus: {
    fontSize: 9,
    fontWeight: 800,
    padding: "2px 6px",
    borderRadius: 4,
    letterSpacing: ".3px",
    whiteSpace: "nowrap",
    lineHeight: 1.2,
    textTransform: "uppercase",
  },

  activityStatusCompleted: {
    background: "rgba(74,155,99,0.15)",
    color: "#2e7a45",
  },

  activityStatusFailed: {
    background: "rgba(220,38,38,0.12)",
    color: "#b91c1c",
  },

  activityLink: {
    color: "#9a6a18",
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
  },
};

export default SwapInWallet;